import React, { useRef, useEffect, useState, useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import StaffLine from '../StaffLine';
import { Box } from '@mui/material';
import Phrase from 'Models/Phrase';
import { range } from 'lodash';
import EventStream from 'Models/EventStream';
import ITimeKeeper from 'Models/ITimeKeeper';
import { TEMPO_SVG_HEIGHT } from 'Utils/Constants';
// import CursorSelector from './CursorSelector';

type StaffProps = {
  midiStream: EventStream;
  timeKeeper: ITimeKeeper;
  phrases: Phrase[];
  topPhraseStartTimestamp: number;
  bottomPhraseStartTimestamp: number;
  scheduler: {
    addPauseCallback: (cb: (countInMeasure: number) => void) => void;
    addUnpauseCallback: (
      cb: (countInTicks: number, startMeasure: number, ticksPerMeasure: number) => void,
    ) => void;
  };
  updateKey: number;
  cursorIsVisible: boolean;
  scrollPosition?: number;
  setScrollPosition?: React.Dispatch<React.SetStateAction<number>>;
};

const Staff: React.FC<StaffProps> = ({
  midiStream,
  timeKeeper,
  phrases,
  topPhraseStartTimestamp,
  bottomPhraseStartTimestamp,
  scheduler,
  updateKey,
  cursorIsVisible,
  scrollPosition,
  setScrollPosition,
}) => {
  const matches = useMediaQuery('(max-width:1450px)');
  const [isPlaying, setIsPlaying] = useState(false);
  const [topPhraseCountInTimestamp, setTopPhraseCountInTimestamp] = React.useState(-1);
  const [bottomPhraseCountInTimestamp, setBottomPhraseCountInTimestamp] = React.useState(0);

  const duration = 1000;

  const perPhraseDivrefs = useRef(
    range(phrases.length).map(() => React.createRef<HTMLDivElement>()),
  );

  useEffect(() => {
    perPhraseDivrefs.current.forEach((phraseRef, index) => {
      if (!perPhraseDivrefs.current[index]) {
        perPhraseDivrefs.current[index] = React.createRef<HTMLDivElement>();
      }
    });

    let aggLength = 0;
    phrases.forEach((phrase, index) => {
      const numPhrases = phrase.musicXML.measures.length;
      const timeSignatureNumerator = phrase.musicXML.timeSignatures[0]?.numerator;
      const timeSignatureDenominator = phrase.musicXML.timeSignatures[0]?.denominator;
      if (index == 0) {
        if (phrase.getTimesigIterator()) {
          midiStream.addEvents2(phrase.getTimesigIterator(), aggLength); //numPhrases*index)
        }
      } else {
        if (phrase.getIterator()) {
          midiStream.addEvents2(
            phrase.getIterator(),
            aggLength - phrases[0].getPickUpMeasureOffset(),
          ); //numPhrases*index)
        }
      }
      aggLength += (numPhrases * timeSignatureNumerator) / timeSignatureDenominator;
    });
  }, []);

  useEffect(() => {
    // TODO unmount logic? Need to un-register from the scheduler later
    if (scheduler) {
      scheduler.addPauseCallback((countInMeasure: number) => {
        setTopPhraseCountInTimestamp(countInMeasure);
        setBottomPhraseCountInTimestamp(countInMeasure);
        setIsPlaying(false);
      });
      scheduler.addUnpauseCallback(
        (_countInTicks: number, _startMeasure: number, _ticksPerMeasure: number) => {
          setTopPhraseCountInTimestamp(-1);
          setBottomPhraseCountInTimestamp(-1);
          setIsPlaying(true);
        },
      );
    }
  }, [scheduler, updateKey]);

  // const defaultStyle = {
  //   transition: `opacity ${duration}ms ease-in-out`,
  //   opacity: 0,
  // }

  const defaultStyle = useMemo(
    () => ({
      transition: `opacity ${duration}ms ease-in-out`,
      opacity: 0,
    }),
    [],
  );
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Box sx={{ margin: 0, width: '90vw', height: matches ? '400px' : '500px' }} ref={containerRef}>
      {phrases.map((phrase, index) => {
        // Figure out what these need to be!!
        const timeSignature = index === 0;
        const phraseStartTimestamp =
          index === 0 ? topPhraseStartTimestamp : bottomPhraseStartTimestamp;
        const countInTimestamp =
          index === 0 ? topPhraseCountInTimestamp : bottomPhraseCountInTimestamp;

        return (
          <Box key={index} sx={{ marginBottom: index == 0 ? `${TEMPO_SVG_HEIGHT}px` : 'unset' }}>
            <PhraseComponent
              key={index}
              phrase={phrases[index]}
              index={index}
              divRef={perPhraseDivrefs.current[index]}
              matches={matches}
              timeKeeper={timeKeeper}
              isPlaying={isPlaying}
              cursorIsVisible={cursorIsVisible}
              defaultStyle={defaultStyle}
              timeSignature={timeSignature}
              phraseStartTimestamp={phraseStartTimestamp}
              countInTimestamp={countInTimestamp}
              scrollPosition={scrollPosition}
              setScrollPosition={setScrollPosition}
            />
          </Box>
        );
      })}
      <Box>
        <i style={{ fontSize: 12 }}>For educational purposes only</i>
      </Box>
      <Box sx={{ height: '70px' }} />
    </Box>
  );
};

export default Staff;

interface PhraseComponentProps {
  phrase: Phrase;
  index: number;
  divRef: React.RefObject<HTMLDivElement | null>;
  matches: boolean;
  timeKeeper: ITimeKeeper;
  isPlaying: boolean;
  cursorIsVisible: boolean;
  defaultStyle: React.CSSProperties;
  timeSignature: boolean;
  phraseStartTimestamp: number;
  countInTimestamp: number;
  scrollPosition?: number;
  setScrollPosition?: React.Dispatch<React.SetStateAction<number>>;
}

const PhraseComponent: React.FC<PhraseComponentProps> = ({
  phrase,
  index,
  divRef: _divRef,
  matches: _matches,
  timeKeeper,
  isPlaying,
  cursorIsVisible,
  defaultStyle,
  timeSignature,
  phraseStartTimestamp,
  countInTimestamp,
  scrollPosition,
  setScrollPosition,
}) => {
  const [containerHeight, setContainerHeight] = useState(400); // Default height
  const svgRef = useRef<HTMLDivElement>(null);
  const staffLineRef = useRef<HTMLDivElement>(null!); // Dedicated ref for StaffLine with non-null assertion

  let phraseSelect;
  if (index == 0) phraseSelect = phrase?.svgTimesigURL || '';
  else {
    phraseSelect = phrase?.svgURL || '';
  }

  // Effect to measure SVG height when it loads
  useEffect(() => {
    const measureSvgHeight = () => {
      if (svgRef.current) {
        const svgElement = svgRef.current.querySelector('svg');
        if (svgElement) {
          // Get the SVG's viewBox or natural dimensions
          const viewBox = svgElement.getAttribute('viewBox');
          if (viewBox) {
            const [, , , height] = viewBox.split(' ').map(Number);
            // Add minimal padding (just a small buffer for cursor and spacing)
            const calculatedHeight = Math.max(height + 30, 200); // Minimum 150px, +30px padding
            setContainerHeight(calculatedHeight);
          } else {
            // Fallback to getBoundingClientRect if no viewBox
            const rect = svgElement.getBoundingClientRect();
            if (rect.height > 0) {
              const calculatedHeight = Math.max(rect.height + 30, 200);
              setContainerHeight(calculatedHeight);
            }
          }
        }
      }
    };

    // Initial measurement
    measureSvgHeight();

    // Set up mutation observer to watch for SVG changes (in case ReactSVG loads asynchronously)
    const observer = new MutationObserver(measureSvgHeight);
    if (svgRef.current) {
      observer.observe(svgRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['viewBox', 'width', 'height'],
      });
    }

    // Cleanup observer
    return () => observer.disconnect();
  }, [phraseSelect, phrase]); // Re-run when phrase changes

  return (
    <Box key={index} sx={{ height: `${containerHeight}px` }}>
      <div ref={svgRef}>
        <StaffLine
          style={{ ...defaultStyle, opacity: 1 }}
          timeSig={timeSignature}
          divRef={staffLineRef}
          phrase={phrase}
          timeKeeper={timeKeeper}
          startTimestamp={phraseStartTimestamp}
          isPlaying={isPlaying}
          countInTimestamp={countInTimestamp}
          cursorIsVisible={cursorIsVisible}
          phraseSvgUrl={phraseSelect}
          phraseIndex={index}
          scrollPosition={scrollPosition}
          setScrollPosition={setScrollPosition}
          isRepertoireStaff={true}
        />
      </div>
    </Box>
  );
};
